import { t } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import { FlashList, FlashListRef } from "@shopify/flash-list";
import graphql from "babel-plugin-relay/macro";
import { format, parse } from "date-fns";
import { enUS, fr } from "date-fns/locale";
import { useLayoutEffect, useMemo, useRef } from "react";
import { usePaginationFragment } from "react-relay";

import { Button } from "@whoosh/design-system/src/components/controls";
import { Box, Text } from "@whoosh/design-system/src/components/primitive";
import { getNounOverride } from "@whoosh/utilities/src/FormatNounOverride/getNounOverride";
import { areDateStringsSameMonth } from "@whoosh/utilities/src/dates";

import { EventListEmptyState } from "./EventListEmptyState";
import { NewPatronEventCardWrapper } from "./NewPatronEventCardWrapper";
import { UpcomingEventsDepartment_department$key } from "./__generated__/UpcomingEventsDepartment_department.graphql";

export const UpcomingEventsDepartment = ({
  fragmentRef,
  ListHeaderComponent,
}: {
  fragmentRef: UpcomingEventsDepartment_department$key;
  ListHeaderComponent: React.ReactNode;
}) => {
  const { i18n } = useLingui();
  const { data, loadNext, hasNext, isLoadingNext } = usePaginationFragment(
    UpcomingEventsDepartmentQueryString,
    fragmentRef
  );

  const events = useMemo(
    () => data.upcomingEvents?.edges || [],
    [data.upcomingEvents]
  );
  const eventNounPlural = getNounOverride(
    data.club?.copyOverrides?.event?.plural,
    t`Events`
  );

  const listRef = useRef<FlashListRef<(typeof events)[number]>>(null);

  useLayoutEffect(() => {
    if (listRef.current) {
      listRef.current.scrollToOffset({
        animated: true,
        offset: 0,
      });
    }
  }, [fragmentRef]);

  const handleLoadMore = () => {
    if (hasNext && !isLoadingNext) {
      loadNext(20);
    }
  };

  if (events.length === 0) {
    return <EventListEmptyState />;
  }

  const renderFooter = () => {
    return hasNext ? (
      <Box
        width={96}
        height={296}
        flex={1}
        alignItems={"center"}
        justifyContent={"center"}
      >
        <Button
          variant="white"
          isLoading={true}
          accessibilityLabel={t`Loading button`}
        />
      </Box>
    ) : null;
  };

  return (
    <FlashList
      testID="card-scroller"
      data={events}
      ref={listRef}
      showsVerticalScrollIndicator={false}
      contentContainerStyle={{ width: "100%" }}
      style={{ flex: 1, width: "100%" }}
      keyExtractor={(item, index) => item?.node?.id ?? String(index)}
      maintainVisibleContentPosition={{ disabled: true }}
      ListHeaderComponent={
        <>
          {ListHeaderComponent}
          <Text mb={6} variant="mainTitle3" width="100%">
            {`${t`Upcoming`} ${eventNounPlural}`}
          </Text>
        </>
      }
      onEndReached={handleLoadMore}
      onEndReachedThreshold={0.5}
      ListFooterComponent={renderFooter()}
      renderItem={({ item, index }) => {
        if (!item?.node) {
          return null;
        }

        const prevItemStartOn = events[index - 1]?.node?.startOn;
        const currentStartOn = item.node.startOn;
        const areDatesSameMonth =
          prevItemStartOn &&
          currentStartOn &&
          areDateStringsSameMonth(prevItemStartOn, currentStartOn);

        const currentMonthAndYear = format(
          parse(currentStartOn, "yyyy-MM-dd", new Date()),
          "MMMM yyyy",
          { locale: i18n.locale === "fr" ? fr : enUS }
        );

        return (
          <Box mb={4}>
            {areDatesSameMonth ? null : (
              <Text variant="mainHeading" mb={4}>
                {currentMonthAndYear}
              </Text>
            )}
            <NewPatronEventCardWrapper
              isFeatured={false}
              eventCardWrapperRef={item.node}
            />
          </Box>
        );
      }}
    />
  );
};

export const UpcomingEventsDepartmentQueryString = graphql`
  fragment UpcomingEventsDepartment_department on Department
  @refetchable(queryName: "UpcomingEventsDepartmentQuery") {
    club {
      copyOverrides {
        event {
          plural
        }
      }
    }
    upcomingEvents: events(
      first: $count
      after: $cursor
      dateGte: $startDate
      dateLte: $endDate
      search: $search
      tagIds: $eventTags
    ) @connection(key: "UpcomingEventsDepartment_upcomingEvents") {
      edges {
        node {
          id
          startOn
          ...NewPatronEventCardWrapper
        }
      }
      pageInfo {
        endCursor
        hasNextPage
      }
    }
  }
`;
